import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Cell } from "react-table";
import { CUSTOM_FIELD_TYPE, ContractGroupInput, ContractGroupListModel, CustomFieldModel } from "src/api/contractGroup/contractgroup-type";
import { BaseTooltip } from "src/components";
import Link from "src/components/PartnerLink";
import { PagePath } from "src/pages/product/details";
import { ViewYmdFormat, YmdFormat, formatLargeNumberString } from "src/utils";
import { CONTRACT_GROUP_STATUS_OPTIONS } from "../../contractgroup-constant";
import { useContractGroup } from "../../hooks/useContractGroup";

const OriginalContractGroupColumnsFirst = [
  {
    Header: "계약그룹 ID", //
    accessor: "groupId",
    width: 120,
    Cell: ({ value }: Cell<ContractGroupInput>) => {
      return (
        <Link className="text-underline" to={PagePath.contractGroup.detail.replace(":id", value)}>
          {value || "-"}
        </Link>
      );
    },
  },
  {
    Header: "상태", //
    accessor: "groupStatus",
    width: 80,
    Cell: ({ value }: Cell<ContractGroupListModel>) => (
      <p className="w-100 text-center">{CONTRACT_GROUP_STATUS_OPTIONS.find((option) => option.value === value)?.label || "-"}</p>
    ),
  },
  {
    Header: "계약그룹명", //
    accessor: "groupName",
    width: 160,
    Cell: ({ value }: Cell<ContractGroupListModel>) => <BaseTooltip type="large" tooltip={value} contents={value || ""} />,
  },
  {
    Header: "계약자(법인명/상호)", //
    accessor: "orgDisplayName",
    width: 160,
    Cell: ({ value }: Cell<ContractGroupListModel>) => <BaseTooltip type="large" tooltip={value} contents={value || ""} />,
  },
  {
    Header: "공간임대", //
    accessor: "fullCourtCount",
    width: 80,
    Cell: ({ value }: Cell<ContractGroupListModel>) => <p className="w-100 text-right">{value || "0"}</p>,
  },
  {
    Header: "공간예약", //
    accessor: "timeCourtCount",
    width: 80,
    Cell: ({ value }: Cell<ContractGroupListModel>) => <p className="w-100 text-right">{value || "0"}</p>,
  },
  {
    Header: "비상주", //
    accessor: "nonResidentOfficeCount",
    width: 80,
    Cell: ({ value }: Cell<ContractGroupListModel>) => <p className="w-100 text-right">{value || "0"}</p>,
  },
];

const OriginalContractGroupColumnsSecond = [
  {
    Header: "이용시작일시", //
    accessor: "groupStartDate",
    width: 160,
    // 데이터 값에 날짜+시간을 직접 노출하고 호버(BaseTooltip type="date")는 제거
    Cell: ({ value }: Cell<ContractGroupListModel>) => {
      return value ? (
        <p className="w-100 text-center">{moment(value).format(YmdFormat.YYYY_MM_DD_HH_MM)}</p>
      ) : (
        <p className="w-100 text-center">-</p>
      );
    },
  },
  {
    Header: "이용종료일시", //
    accessor: "groupEndDate",
    width: 160,
    Cell: ({ value }: Cell<ContractGroupListModel>) => {
      return value ? (
        <p className="w-100 text-center">{moment(value).format(YmdFormat.YYYY_MM_DD_HH_MM)}</p>
      ) : (
        <p className="w-100 text-center">-</p>
      );
    },
  },
  {
    Header: "최초 등록일시", //
    accessor: "createdDate",
    width: 160,
    Cell: ({ value }: Cell<ContractGroupListModel>) => {
      return value ? (
        <p className="w-100 text-center">{moment(value).format(YmdFormat.YYYY_MM_DD_HH_MM)}</p>
      ) : (
        <p className="w-100 text-center">-</p>
      );
    },
  },
  {
    Header: "최종 수정일시", //
    accessor: "modifiedDate",
    width: 160,
    Cell: ({ value }: Cell<ContractGroupListModel>) => {
      return value ? (
        <p className="w-100 text-center">{moment(value).format(YmdFormat.FULL)}</p>
      ) : (
        <p className="w-100 text-center">-</p>
      );
    },
  },
];

// 커스텀 필드를 react-table 컬럼 형태로 변환
const createCustomFieldColumns = (customFields: CustomFieldModel[]) => {
  return customFields
    .filter((field) => field.isVisible && !field.isDeleted)
    .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
    .map((field) => ({
      Header: () => <span className="custom-field-header">{field.fieldName}</span>,
      accessor: `fieldId_${field.fieldId}` as any, // 실제로는 사용되지 않지만 유니크한 키로 활용
      width: field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR ? 110 : 160, // 캘린더 유형은 100, 그 외 기본 160
      Cell: ({ row }: Cell<ContractGroupListModel>) => {
        const originalData = row.original;

        // inputList에서 해당 fieldId의 데이터 찾기
        const inputData = originalData.inputList?.find((input) => input.fieldId === field.fieldId);

        if (!inputData) {
          // 캘린더 유형은 값이 없어도(-) 중앙 정렬 유지
          return field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR ? (
            <p className="custom-field w-100 text-center">-</p>
          ) : (
            <span className="custom-field">-</span>
          );
        }

        let displayValue = inputData.inputValue || "";

        // optionList가 있는 경우 (라디오, 체크박스 등)
        if (inputData.optionList && inputData.optionList.length > 0) {
          if (field.optionList) {
            // 선택된 옵션들의 이름 찾기
            const selectedOptionNames = inputData.optionList
              .map((selectedOption) => {
                const option = field.optionList?.find((opt) => opt.optionId === selectedOption.optionId);
                return option?.optionName;
              })
              .filter(Boolean); // undefined 제거

            displayValue = selectedOptionNames.join(", ");
          }
        }

        // 필드 타입에 따른 값 포맷팅
        if (displayValue) {
          switch (field.fieldType) {
            case CUSTOM_FIELD_TYPE.NUMBER:
              // 숫자 타입: 천단위 콤마 적용 (큰 숫자 정밀도 손실 방지)
              const formattedValue = formatLargeNumberString(displayValue);
              if (formattedValue) {
                return <BaseTooltip type="large" className="custom-field text-right w-100" tooltip={formattedValue} contents={formattedValue} />;
              }
              break;

            case CUSTOM_FIELD_TYPE.CALENDAR:
              // 캘린더 타입: 시간 입력이 없으므로 날짜(YYYY-MM-DD)만 노출하고 호버는 제거
              if (moment(displayValue).isValid()) {
                return <p className="custom-field w-100 text-center">{moment(displayValue).format(ViewYmdFormat.YYYY_MM_DD)}</p>;
              }
              break;

            default:
              // TEXT, URL, RADIO, CHECKBOX 등 기본 타입들
              break;
          }
        }

        // 캘린더 유형은 값이 없거나 유효하지 않아도(-) 중앙 정렬 유지
        if (field.fieldType === CUSTOM_FIELD_TYPE.CALENDAR) {
          return <p className="custom-field w-100 text-center">{displayValue || "-"}</p>;
        }

        // return <span className="custom-field ellipsis">{displayValue || "-"}</span>;
        return <BaseTooltip className="custom-field" contents={displayValue || "-"} />;
      },
    }));
};

export const useContractGroupColumns = () => {
  const { getCtGroupCustomField } = useContractGroup();
  const [customFields, setCustomFields] = useState<CustomFieldModel[]>([]);
  const customFieldColumns = createCustomFieldColumns(customFields);

  // 커스텀 필드 조회
  useEffect(() => {
    const fetchCustomFields = async () => {
      setCustomFields(await getCtGroupCustomField());
    };

    fetchCustomFields();
  }, []);

  // 최종 컬럼 배열 생성
  const contractGroupColumns = useMemo(() => {
    return [...OriginalContractGroupColumnsFirst, ...customFieldColumns, ...OriginalContractGroupColumnsSecond];
  }, [customFields]);

  return {
    contractGroupColumns,
    customFields,
  };
};
